import { makeAutoObservable, onBecomeUnobserved, toJS } from "mobx";
import module from "./module";
import { supabaseSelect, wrap } from "../util/supabase-wrapper";
import { supabase, definitions } from "./supabase/client";
import { globalStores } from "./globalStores";
import { v4 } from "uuid";

const logger = console;

type FileType = "png" | "jpeg" | "webp" | "gif" | "mp4" | "INFER";

const fileNameMapping = [
  { qnsFile: "question-attachment", name: "question" },
  { optionAFile: "question-answer-A", name: "answerA" },
  { optionBFile: "question-answer-B", name: "answerB" },
  { optionCFile: "question-answer-C", name: "answerC" },
  { optionDFile: "question-answer-D", name: "answerD" },
];

const attachmentNameToColName = {
  "question-attachment": "qnsFile",
  "question-answer-A": "optionAFile",
  "question-answer-B": "optionBFile",
  "question-answer-C": "optionCFile",
  "question-answer-D": "optionDFile",
} as const;

type AttachmentName = keyof typeof attachmentNameToColName;


export class EngagementPanel {
  // private pollingIntervalId: ReturnType<typeof setInterval> | undefined =
  //   undefined;
  // private pollingMode = false;
  engagementPanel: Partial<definitions["EngagementPanel"]> = {};
  module: Partial<definitions["Module"]> = {};
  assetUrls: {
    answerA?: string;
    answerB?: string;
    answerC?: string;
    answerD?: string;
    question?: string;
  } = {};
  assets: {
    questionFile?: File;
    optionAFile?: File;
    optionBFile?: File;
    optionCFile?: File;
    optionDFile?: File;
  } = {};

  // Track if assets failed to load to avoid stuck skeleton loaders
  failedAssets: {
    question?: boolean;
    answerA?: boolean;
    answerB?: boolean;
    answerC?: boolean;
    answerD?: boolean;
  } = {};

  isLoadingData: boolean = false;

  // Monotonically increasing counter; incremented on every init/initFromInput
  // so that in-flight async operations from a previous load are discarded.
  private _loadGeneration: number = 0;

  constructor() {
    makeAutoObservable(this, {
      // Keep _loadGeneration out of MobX tracking — it's an internal guard, not UI state.
      _loadGeneration: false,
    } as any);
  }

  get isAssetsLoading() {
    return this.isLoadingData;
  }

  /**
   * Copy state from another EngagementPanel instance.
   * @param options.skipRefresh - If true, skip the DB refresh (use for live lessons with preloaded data to avoid an extra network call).
   */
  initFromInput(
    inputEpanel: EngagementPanel,
    options?: { skipRefresh?: boolean }
  ) {
    const gen = ++this._loadGeneration;
    this.engagementPanel = inputEpanel.engagementPanel;
    this.module = inputEpanel.module;
    this.assetUrls = inputEpanel.assetUrls;
    this.assets = inputEpanel.assets;
    this.failedAssets = inputEpanel.failedAssets;
    // Panel data is present synchronously from `inputEpanel`, so the loading
    // overlay must not show. This also clears a stale isLoadingData=true left by
    // an init() that this call supersedes: initFromInput bumps _loadGeneration,
    // which orphans that init()'s own `if (gen === _loadGeneration)` reset — so
    // without this the loader sticks on forever (seen on the teacher, whose panel
    // switches interleave init() and initFromInput()).
    this.isLoadingData = false;
    if (options?.skipRefresh) {
      this.loadAssetsIfNeeded(gen);
      return;
    }
    if (this.engagementPanel.id) {
      this.pullDetails(gen).then(() => {
        if (gen === this._loadGeneration) this.loadAssetsIfNeeded(gen);
      });
    } else {
      this.loadAssetsIfNeeded(gen);
    }
  }

  /** Triggers asset download if URLs are present but files haven't been fetched yet. */
  loadAssetsIfNeeded(gen?: number) {
    const hasUrls = Object.values(this.assetUrls).some(Boolean);
    const hasFiles = Object.values(this.assets).some(Boolean);
    if (hasUrls && !hasFiles) {
      this.pullAssets(gen);
    }
  }

  static async getAllOrderNumbers(moduleId: number) {
    return wrap(
      await supabase
        .from("EngagementPanel")
        .select("id,order")
        .match({ module: moduleId })
    );
  }

  updateFromDBInput(
    inputEpanelDBData: Partial<definitions["EngagementPanel"]>
  ) {
    if (inputEpanelDBData) {
      this.setAllDetailsWithoutModule([inputEpanelDBData]);
    }
  }

  async getFileUploadPath(
    type: AttachmentName,
    isNewFile: true
  ): Promise<string>;
  async getFileUploadPath(
    type: AttachmentName,
    isNewFile: false
  ): Promise<string | undefined>;
  async getFileUploadPath(
    type: AttachmentName,
    /**When newFile is set to true, create a new hash */
    isNewFile: boolean
  ) {
    const colName = attachmentNameToColName[type];
    let fileName: string | undefined = undefined;
    const expectedVal = this.engagementPanel[colName] ?? undefined;
    if (typeof expectedVal !== "string" && typeof expectedVal !== "undefined") {
      throw new Error(
        "curFile must be string or undefined, have you run the migations yet?"
      );
    } else {
      fileName = expectedVal as string | undefined;
    }

    if (!this.engagementPanel?.id) {
      throw new Error(
        "Engagement Panel ID must be defined! Has it been initialized yet?"
      );
    }

    if (!isNewFile) {
      return fileName;
    }
    fileName = v4();
    //Set the fileName
    await supabase
      .from("EngagementPanel")
      .update({ [attachmentNameToColName[type]]: fileName })
      .eq("id", this.engagementPanel.id);

    await this.pullDetails();

    return fileName;
  }

  //TODO: Support ogg files and whatnot
  //TODO: Make the INFER option more explicit
  static getMimeType(type: FileType, attachment: File | Buffer) {
    const mimeType =
      type === "jpeg"
        ? "image/jpeg"
        : type === "png"
        ? "image/png"
        : type === "webp"
        ? "image/webp"
        : type === "gif"
        ? "image/gif"
        : type === "mp4"
        ? "video/mp4"
        : "type" in attachment
        ? attachment.type
        : undefined;

    return mimeType;
  }

  static getFileExtension(mimeType: string) {
    const extension =
      mimeType === "image/jpeg"
        ? "jpeg"
        : mimeType === "image/png"
        ? "png"
        : mimeType === "image/webp"
        ? "webp"
        : mimeType === "image/gif"
        ? "gif"
        : mimeType === "video/mp4"
        ? "mp4"
        : mimeType;

    return extension;
  }

  //TODO: Make type forced like 'png' | 'jpeg' something like that
  async setQuestionAttachment(
    attachment: File | Buffer | null | undefined,
    type: FileType
  ) {
    logger.log("Updating question attachment!");
    const uploadPath = await this.getFileUploadPath(
      "question-attachment",
      true
    );

    if (attachment === null || attachment === undefined) {
      wrap(await supabase.storage.from("teaching-assets").remove([uploadPath]));
      delete this.assets.questionFile;
      delete this.assetUrls.question;
    } else {
      const mimeType = EngagementPanel.getMimeType(type, attachment);

      //TODO: Make storage bucket private so only students/teachers can read buckets
      wrap(
        await supabase.storage
          .from("teaching-assets")
          .upload(uploadPath, attachment, {
            contentType: mimeType,
            upsert: true,
          })
      );
    }
    //TODO: Make storage bucket private so only students/teachers can read buckets
    //TODO: Make test case such that question attachements CAN be updated
    logger.log("PULLING ASSETS");
    this.pullAssets();
    logger.log("ASSETS PULLED");

    return;
  }

  //TODO: Prevent teachers from changing the correct answer to an engagement panel - otherwise student answers can go from correct to wrong
  /**Returns true if answered correctly, otherwise false */
  //this one is for StudentLesson.tsx, when the student selects an answer, this method will run to update Supabase with the data.
  async answerEngagementPanel(
    answerSelected: "A" | "B" | "C" | "D",
    talkingPartyRoom?: number
  ): Promise<boolean> {
    if (!globalStores.childStore.childAccount.id) {
      throw new Error(
        "Must have an active child account before answering engagement panel!"
      );
    }

    // Use upsert now that the unique constraint exists in the database
    const upsertData = {
      engagementPanel: this.engagementPanel.id,
      student: globalStores.childStore.childAccount.id,
      answerSelected,
      talkingPartyRoom,
    };

    const { error } = await supabase
      .from("EngagementPanelResult")
      .upsert(upsertData, {
        onConflict: talkingPartyRoom
          ? "engagementPanel,student,talkingPartyRoom"
          : "engagementPanel,student",
      });

    if (error) {
      logger.error("Error upserting engagement panel result:", error);
      throw error;
    }

    if (answerSelected === this.engagementPanel.answer) {
      return true;
    }
    return false;
  }

  async setAnswerAttachment(
    attachment: File | Buffer | null | undefined,
    type: FileType,
    answerKey: "A" | "B" | "C" | "D"
  ) {
    const uploadPath = await this.getFileUploadPath(
      `question-answer-${answerKey}`,
      true
    );

    if (attachment === null || attachment === undefined) {
      wrap(await supabase.storage.from("teaching-assets").remove([uploadPath]));
      delete this.assets[`option${answerKey}File`];
      delete this.assetUrls[`answer${answerKey}`];
    } else {
      const mimeType = EngagementPanel.getMimeType(type, attachment);

      //TODO: Make storage bucket private so only students/teachers can read buckets
      wrap(
        await supabase.storage
          .from("teaching-assets")
          .upload(uploadPath, attachment, {
            contentType: mimeType,
            upsert: true,
          })
      );
    }

    this.pullAssets();

    return;
  }

  async getAssetURLs(engagementPanel: any) {
    const getUrl = async (
      fileUploadPath: Parameters<typeof this.getFileUploadPath>[0]
    ) => {
      const resolvedPath = await this.getFileUploadPath(fileUploadPath, false);
      if (!resolvedPath) return undefined;

      // All asset images will have webps as requested.
      const isImageAttachment =
        fileUploadPath === "question-attachment" ||
        fileUploadPath.startsWith("question-answer-");

      if (isImageAttachment) {
        // If it's an image, we want the .webp version.
        // We strip any existing image extension and add .webp, or just add it.
        const webpPath = resolvedPath.endsWith(".webp") 
          ? resolvedPath 
          : resolvedPath.replace(/\.(png|jpe?g|gif)$/i, "") + ".webp";
          
        return supabase.storage.from("teaching-assets").getPublicUrl(webpPath)
          .data?.publicUrl;
      }

      return supabase.storage.from("teaching-assets").getPublicUrl(resolvedPath)
        .data?.publicUrl;
    };
    let result: any = {};

    await Promise.all(
      fileNameMapping.map(async (obj) => {
        let key = Object.keys(obj)[0];
        if (engagementPanel[key]) {
          result[obj.name] = await getUrl((obj as any)[key]);
        }
      })
    );
    return result;
  }

  async setType(type: NonNullable<definitions["EngagementPanel"]["type"]>) {
    await supabase
      .from("EngagementPanel")
      .update({
        type,
      })
      .match({
        id: this.engagementPanel.id,
      })
      .throwOnError();

    await this.pullDetails();

    return this;
  }

  async setDuration(
    duration: NonNullable<definitions["EngagementPanel"]["duration"]>
  ) {
    await supabase
      .from("EngagementPanel")
      .update({
        duration,
      })
      .match({
        id: this.engagementPanel.id,
      })
      .throwOnError();

    await this.pullDetails();

    return this;
  }

  async setQnsAssetDesc(
    qnsAssetDesc: NonNullable<definitions["EngagementPanel"]["qnsAssetDesc"]>
  ) {
    await supabase
      .from("EngagementPanel")
      .update({
        qnsAssetDesc,
      })
      .match({
        id: this.engagementPanel.id,
      })
      .throwOnError();

    await this.pullDetails();

    return this;
  }

  async setQuestionText(
    qns: NonNullable<definitions["EngagementPanel"]["qns"]>
  ) {
    await supabase
      .from("EngagementPanel")
      .update({
        qns,
      })
      .match({
        id: this.engagementPanel.id,
      })
      .throwOnError();

    await this.pullDetails();

    return this;
  }

  async setAnswer(
    answer: NonNullable<definitions["EngagementPanel"]["answer"]>
  ) {
    await supabase
      .from("EngagementPanel")
      .update({
        answer,
      })
      .match({
        id: this.engagementPanel.id,
      });

    await this.pullDetails();

    return this;
  }

  async setAnswerText(answer: "A" | "B" | "C" | "D", text: string) {
    const updateField =
      answer === "A"
        ? "optionA"
        : answer === "B"
        ? "optionB"
        : answer === "C"
        ? "optionC"
        : answer === "D"
        ? "optionD"
        : undefined;

    if (!updateField) throw new Error("Setting text for invalid answer!");

    await supabase
      .from("EngagementPanel")
      .update({
        [updateField]: text,
      })
      .match({
        id: this.engagementPanel.id,
      })
      .throwOnError();

    await this.pullDetails();

    return this;
  }

  async setCurrentPanelEditor(curEPanelID?: number, teacherID?: number | null) {
    await supabase
      .from("EngagementPanel")
      .update({
        currentPanelEditor: teacherID ?? null,
      })
      .match({
        id: curEPanelID ?? this.engagementPanel.id,
      })
      .throwOnError();
  }

  // async resetCurrentPanelEditor() {

  // }

  async setBrijStandard(
    standard: "brijPinyin" | "brijGrammar" | "brijVocab" | "brijRadicals",
    value: boolean
  ) {
    await supabase
      .from("EngagementPanel")
      .update({
        [standard]: value,
      })
      .match({
        id: this.engagementPanel.id,
      })
      .throwOnError();

    await this.pullDetails();

    return this;
  }

  async setBloomsStandard(
    standard:
      | "bloomRemembering"
      | "bloomUnderstanding"
      | "bloomApplying"
      | "bloomAnalyzing"
      | "bloomEvaluating"
      | "bloomCreating",
    value: boolean
  ) {
    await supabase
      .from("EngagementPanel")
      .update({
        [standard]: value,
      })
      .match({
        id: this.engagementPanel.id,
      })
      .throwOnError();

    await this.pullDetails();

    return this;
  }

  static async getNextOrderNumber(moduleId: number) {
    const curOrderNums = await EngagementPanel.getAllOrderNumbers(moduleId);
    const onlyNums = curOrderNums.map((curOrderNum) => curOrderNum.order);
    const curMaxOrder = onlyNums.length === 0 ? 0 : Math.max(...onlyNums);
    return Math.ceil(curMaxOrder) + 1;
  }

  // async initFromModule(id: number) {
  //   const curModEPanels = globalStores.curModule.engagementPanels;
  //   for (let i = 0; i < curModEPanels.length; i++) {
  //     if (curModEPanels[i].id === id) {

  //       this.engagementPanel = curModEPanels

  //       return this
  //     }
  //   }
  // }

  async init(id: number) {
    if (this.engagementPanel.id === id && !this.isLoadingData) {
      return this;
    }

    const gen = ++this._loadGeneration;
    this.isLoadingData = true;
    this.assets = {};
    this.failedAssets = {};
    this.setId(id);

    try {
      await this.pullDetails(gen);
      if (gen === this._loadGeneration) {
        this.loadAssetsIfNeeded(gen);
      }
    } catch (error) {
      logger.error("EngagementPanel init failed:", error);
    }

    if (gen === this._loadGeneration) {
      this.isLoadingData = false;
    }
    return this;
  }

  /** Initializes the panel from data already fetched from the DB, saving a network call */
  async initFromDBData(data: Partial<definitions["EngagementPanel"]>) {
    const gen = ++this._loadGeneration;
    this.isLoadingData = true;
    this.engagementPanel = data;
    if (data.id) {
      this.setId(data.id);
    }

    try {
      const assetUrls = await this.getAssetURLs(data);
      if (gen !== this._loadGeneration) return this;
      this.setAssets(assetUrls);
      await this.pullAssets(gen);
    } catch (error) {
      logger.error("EngagementPanel initFromDBData failed:", error);
    }

    if (gen === this._loadGeneration) {
      this.isLoadingData = false;
    }
    return this;
  }

  /** Stores DB data + asset URLs without downloading image files. Used for preloading metadata only. */
  async initMetadataOnly(data: Partial<definitions["EngagementPanel"]>) {
    this.engagementPanel = data;
    if (data.id) {
      this.setId(data.id);
    }

    try {
      const assetUrls = await this.getAssetURLs(data);
      this.setAssets(assetUrls);
    } catch (error) {
      logger.error("EngagementPanel initMetadataOnly failed:", error);
    }

    return this;
  }

  private setId(id: number) {
    this.engagementPanel.id = id;
  }

  async remove() {
    await supabase
      .from("EngagementPanel")
      .delete()
      .match({ id: this.engagementPanel.id });

    this._remove();
  }

  async clear() {
    this._remove();
  }

  private async _remove() {
    this.engagementPanel = {};
    this.module = {};
    this.assets = {};
    this.assetUrls = {};
    this.failedAssets = {};
    // this.killSubscriptions();
  }

  //TODO: Prevent this from updating every cycle to reduce network calls
  private setAssets(assetUrls: {
    question: string | undefined;
    answerA: string | undefined;
    answerB: string | undefined;
    answerC: string | undefined;
    answerD: string | undefined;
  }) {
    this.assetUrls = assetUrls;
  }

  private async pullAssets(gen?: number) {
    // If we don't have asset URLs yet, pull them
    if (Object.keys(this.assetUrls).length === 0) {
      await this.pullDetails();
    }

    // If a newer load has started, discard this one
    if (gen !== undefined && gen !== this._loadGeneration) return;

    const promises: Promise<void>[] = [];

    // Question asset
    if (this.assetUrls.question) {
      promises.push(
        getFileFromURL(this.assetUrls.question).then((file) => {
          if (gen !== undefined && gen !== this._loadGeneration) return;
          if (file) {
            if (this.assets.questionFile !== file) {
              this.setQuestionFile(file);
            }
          } else {
            this.failedAssets.question = true;
          }
        })
      );
    } else {
      this.assets.questionFile = undefined;
    }

    // Option A asset
    if (this.assetUrls.answerA) {
      promises.push(
        getFileFromURL(this.assetUrls.answerA).then((file) => {
          if (gen !== undefined && gen !== this._loadGeneration) return;
          if (file) {
            if (this.assets.optionAFile !== file) {
              this.setOptionAFile(file);
            }
          } else {
            this.failedAssets.answerA = true;
          }
        })
      );
    } else {
      this.assets.optionAFile = undefined;
    }

    // Option B asset
    if (this.assetUrls.answerB) {
      promises.push(
        getFileFromURL(this.assetUrls.answerB).then((file) => {
          if (gen !== undefined && gen !== this._loadGeneration) return;
          if (file) {
            if (this.assets.optionBFile !== file) {
              this.setOptionBFile(file);
            }
          } else {
            this.failedAssets.answerB = true;
          }
        })
      );
    } else {
      this.assets.optionBFile = undefined;
    }

    // Option C asset
    if (this.assetUrls.answerC) {
      promises.push(
        getFileFromURL(this.assetUrls.answerC).then((file) => {
          if (gen !== undefined && gen !== this._loadGeneration) return;
          if (file) {
            if (this.assets.optionCFile !== file) {
              this.setOptionCFile(file);
            }
          } else {
            this.failedAssets.answerC = true;
          }
        })
      );
    } else {
      this.assets.optionCFile = undefined;
    }

    // Option D asset
    if (this.assetUrls.answerD) {
      promises.push(
        getFileFromURL(this.assetUrls.answerD).then((file) => {
          if (gen !== undefined && gen !== this._loadGeneration) return;
          if (file) {
            if (this.assets.optionDFile !== file) {
              this.setOptionDFile(file);
            }
          } else {
            this.failedAssets.answerD = true;
          }
        })
      );
    } else {
      this.assets.optionDFile = undefined;
    }

    await Promise.all(promises);
  }

  private setOptionDFile(
    file: File | undefined
  ): File | PromiseLike<File | undefined> | undefined {
    return (this.assets.optionDFile = file);
  }

  private setOptionCFile(
    file: File | undefined
  ): File | PromiseLike<File | undefined> | undefined {
    return (this.assets.optionCFile = file);
  }

  private setOptionBFile(
    file: File | undefined
  ): File | PromiseLike<File | undefined> | undefined {
    return (this.assets.optionBFile = file);
  }

  private setOptionAFile(
    file: File | undefined
  ): File | PromiseLike<File | undefined> | undefined {
    return (this.assets.optionAFile = file);
  }

  private setQuestionFile(
    file: File | undefined
  ): File | PromiseLike<File | undefined> | undefined {
    return (this.assets.questionFile = file);
  }

  // setAllDetailsFromInput() {

  // }

  private setAllDetails(allDetails: any[]) {
    this.module = allDetails[0].module;

    delete allDetails[0].module;

    this.engagementPanel = allDetails[0];

    this.engagementPanel.module = this.module.id;
  }

  private setAllDetailsWithoutModule(allDetailsWithouModule: any[]) {
    this.engagementPanel = allDetailsWithouModule[0];
  }

  private async pullDetails(gen?: number) {
    //TODO: Split into base details and full details, otherwise may get laggy
    if (!this.engagementPanel.id) {
      // throw new Error("Need to set engagement panel ID first!");
      // console.warn("set engagement panel ID is empty! Aborting pullDetails")
      return;
    }

    // this.killSubscriptions();
    // console.warn("pulling details");

    //This supabase select works but it is risky if there are edge cases
    const allDetails = wrap(
      await supabaseSelect(
        "EngagementPanel",
        "*, module:Module(*)",
        this.engagementPanel.id
      )
    );

    // Discard stale results if a newer load has started
    if (gen !== undefined && gen !== this._loadGeneration) return;

    // const allDetails = wrap(
    //   await supabase
    //     .from("EngagementPanel")
    //     .select("*, module:Module(*)")
    //     .match({ id: this.engagementPanel.id })
    // );

    this.setAllDetails(allDetails);

    const assetUrls = await this.getAssetURLs(allDetails[0]);

    if (gen !== undefined && gen !== this._loadGeneration) return;

    this.setAssets(assetUrls);

    return this;
  }
}

const ASSET_FETCH_TIMEOUT_MS = 10000;

async function getFileFromURL(url: string) {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ASSET_FETCH_TIMEOUT_MS);

    let result = await fetch(url, { signal: controller.signal });

    if (!result.ok && url.includes(".webp")) {
      const fallbackUrl = url.replace(/\.webp/g, "");
      console.warn(`WebP fetch failed for ${url}, trying fallback: ${fallbackUrl}`);
      result = await fetch(fallbackUrl, { signal: controller.signal });
    }

    clearTimeout(timeoutId);

    if (!result || !result.ok) return undefined;
    const blob = await result.blob();
    const finalFile = new File([blob], url, {
      type: result.headers.get("Content-Type") ?? "",
    });

    return finalFile;
  } catch (err) {
    console.error(`Error fetching asset from ${url}:`, err);
    return undefined;
  }
}

export default new EngagementPanel();
